import React, { useEffect, useState } from 'react';
import useShippingMethod from '../../components/shipping-method/useShippingMethod';
import { ShippingMethodPrice } from './ShippingMethodPrice';
import WaiveShippingFeeCheckbox from './WaiveShippingFeeCheckbox';
import { WAIVED_SHIPPING_METHOD_AFFIX } from './WaiveShippingFeeCheckbox';

/**
 * Displays the Shipping Method section of the Shipping Information step for a ship-to-office order.
 *
 * @param props.gearOrder - The order
 * @param props.shippingMethod - The order's selected shipping method
 * @param props.setShippingMethod - The method to call when the selected shipping method is changed
 * @param props.isDisabledInputs - A flag indicating if the selections should be disabled
 * @param props.displaySmartPost - A flag indicating if the Smart Post option should be displayed. Default = false
 * @param props.isPoBox - A flag indicating if the delivery address has been confirmed to be a PO Box. Default = false
 * @param props.userInfo - The user details
 */
function ShippingMethodSelection({
	gearOrder,
	shippingMethod,
	setShippingMethod,
	isDisabledInputs,
	displaySmartPost,
	isPoBox,
	isPotentialPoBox,
	userInfo
}) {
	const [availableShippingCharges, loading] = useShippingMethod(gearOrder?.id);
	const [feeWaived, setFeeWaived] = useState(shippingMethod?.includes(WAIVED_SHIPPING_METHOD_AFFIX));

	// The shipping method might include "(Waived)" if an admin waived the fee. Strip that off for display.
	const displayedShippingMethod = shippingMethod?.replace(WAIVED_SHIPPING_METHOD_AFFIX, '');

	// Add or Remove the "Waived" affix when the waive fee checkbox or the selected method changes
	useEffect(() => {
		let selectedMethod = displayedShippingMethod;
		if (feeWaived && availableShippingCharges?.[selectedMethod]) {
			selectedMethod += WAIVED_SHIPPING_METHOD_AFFIX;
		}
		setShippingMethod(buildShippingMethodEvent(selectedMethod));
	}, [availableShippingCharges, displayedShippingMethod, feeWaived, setShippingMethod]);

	// Address is confirmed to be a PO Box. Only show the PO Box option.
	if (isPoBox) {
		return <POBoxStandAlone />;
	}

	let poBoxHelpStyle;
	if (isPotentialPoBox) {
		poBoxHelpStyle = {
			color: '#a94442'
		};
	}

	return (
		<>
			<span>
				Please select the shipping method by which you wish your packs to be shipped. FedEx Ground shipping is
				included at no additional charge. Expedited shipping charges will be required at checkout.
			</span>

			<div className="form-group ">
				<div className="col-sm-12">
					<div className="radio">
						<label>
							<input
								id="formShippingMethod0"
								name="shippingMethod"
								data-fieldname="shippingMethod"
								type="radio"
								value="FedEx Ground"
								checked={!displayedShippingMethod || displayedShippingMethod === 'FedEx Ground'}
								onChange={setShippingMethod}
								disabled={isDisabledInputs}
							/>
							<ShippingMethodPrice shippingMethod="FedEx Ground" shippingCharge={0} />
						</label>
						<span className="help-block small help-block-indent">
							In most cases, standard shipping orders received after 2:30 PM ET, will ship within two
							business days.*
						</span>
					</div>

					<div className="radio">
						<label>
							<input
								id="formShippingMethod1"
								name="shippingMethod"
								data-fieldname="shippingMethod"
								type="radio"
								value="FedEx Second Day"
								checked={displayedShippingMethod === 'FedEx Second Day'}
								onChange={setShippingMethod}
								disabled={isDisabledInputs}
							/>
							<ShippingMethodPrice
								shippingMethod="FedEx Second Day"
								shippingCharge={feeWaived ? 0 : availableShippingCharges?.['FedEx Second Day']}
								loading={loading}
							/>
						</label>
						{displayedShippingMethod === 'FedEx Second Day' && (
							<WaiveShippingFeeCheckbox
								userInfo={userInfo}
								gearOrder={gearOrder}
								style={{ marginTop: '-5px', paddingLeft: '30px' }}
								feeWaived={feeWaived}
								setFeeWaived={setFeeWaived}
							/>
						)}
						<span className="small help-block help-block-indent">
							In most cases, expedited shipping orders received after 2:30 PM ET, will ship the following
							business day.*
						</span>
					</div>

					<div className="radio">
						<label>
							<input
								id="formShippingMethod2"
								name="shippingMethod"
								data-fieldname="shippingMethod"
								type="radio"
								value="FedEx Overnight"
								checked={displayedShippingMethod === 'FedEx Overnight'}
								onChange={setShippingMethod}
								disabled={isDisabledInputs}
							/>
							<ShippingMethodPrice
								shippingMethod="FedEx Overnight"
								shippingCharge={feeWaived ? 0 : availableShippingCharges?.['FedEx Overnight']}
								loading={loading}
							/>
						</label>
						{displayedShippingMethod === 'FedEx Overnight' && (
							<WaiveShippingFeeCheckbox
								userInfo={userInfo}
								gearOrder={gearOrder}
								style={{ marginTop: '-5px', paddingLeft: '30px' }}
								feeWaived={feeWaived}
								setFeeWaived={setFeeWaived}
							/>
						)}
						<span className="small help-block help-block-indent">
							In most cases, expedited shipping orders received after 12:00 PM (noon) ET, will ship the
							following business day.*
						</span>
					</div>

					{displaySmartPost && (
						<div className="radio">
							<label>
								<input
									id="formShippingMethod3"
									name="shippingMethod"
									data-fieldname="shippingMethod"
									type="radio"
									value="FedEx Smart Post"
									checked={displayedShippingMethod === 'FedEx Smart Post'}
									onChange={setShippingMethod}
									disabled={isDisabledInputs}
								/>
								<ShippingMethodPrice shippingMethod="FedEx Smart Post" shippingCharge={0} />
								<span className="small help-block" style={poBoxHelpStyle}>
									Collection Packs being shipped to a PO Box must be shipped Smart Post.
								</span>
							</label>
						</div>
					)}
					<span className="small help-block"></span>
				</div>
			</div>

			<p className="small">* There are occasional scenarios that may delay shipment.</p>
			<p className="small">* Expedited shipping method takes effect once the package leaves the facility.</p>
		</>
	);
}

const POBoxStandAlone = () => {
	return (
		<div className="form-group ">
			<div className="col-sm-12">
				<div className="radio">
					<label>
						<input
							id="formShippingMethod3"
							name="shippingMethod"
							data-fieldname="shippingMethod"
							type="radio"
							value="FedEx Smart Post"
							defaultChecked={true}
						/>
						<ShippingMethodPrice shippingMethod="FedEx Smart Post" shippingCharge={0} />
						<span className="small help-block">
							Collection Packs being shipped to a PO Box must be shipped Smart Post.
						</span>
					</label>
				</div>
			</div>
		</div>
	);
};

/**
 * Create the Event object to pass to the parent's setShippingMethod.
 */
const buildShippingMethodEvent = (shippingMethod) => {
	return {
		target: {
			value: shippingMethod,
			name: 'shippingMethod'
		},
		currentTarget: {
			dataset: {
				fieldname: 'shippingMethod'
			},
			value: shippingMethod
		}
	};
};

export default ShippingMethodSelection;
